import { useEffect, useRef, useState } from "react";
import { Calendar, Filter, X, RotateCcw } from "lucide-react";
import { PERIODS } from "./formatters";

/**
 * Filter bar — period selector + multi-select attribute filters.
 * Dependent facets: `model` is disabled until at least one brand is picked.
 *
 * `onResetAll` (optional): callback that resets EVERYTHING — period,
 * custom date range and all filters. When passed, we render a small
 * "Reiniciar" pill in the header even when no filters are active,
 * because a non-default period alone can also be "state to clear".
 */
export default function FilterBar({
  period, setPeriod, dateRange, setDateRange, filters, setFilters, options,
  onResetAll,
}) {
  const activeFilters = Object.values(filters).reduce((n, v) => n + (v.length || 0), 0);
  const reset = () => setFilters({ brand: [], model: [], branch: [], business_unit: [], channel: [], campaign: [] });

  return (
    <div className="mb-5 p-4 rounded-lg"
      style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
      data-testid="scorecard-filter-bar">
      <div className="flex flex-wrap items-end gap-3">
        {/* Period presets */}
        <div className="flex flex-col gap-1" style={{ flexShrink: 0 }}>
          <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Período</label>
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-white"
            style={{ border: "1px solid #e2e8f0" }} data-testid="period-selector">
            <Calendar size={14} style={{ color: "#64748b", margin: "0 4px 0 6px" }} />
            {PERIODS.map((p) => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className="text-xs font-bold px-3 py-1.5 rounded-md transition-all"
                style={{
                  background: period === p.id ? "#f4c400" : "transparent",
                  color: period === p.id ? "#111827" : "#64748b",
                  cursor: "pointer", border: "none",
                }}
                data-testid={`period-${p.id}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date range — only visible when preset === custom */}
        {period === "custom" && (
          <>
            <FilterInput label="Desde" type="date" value={dateRange.start}
              onChange={(v) => setDateRange((r) => ({ ...r, start: v }))}
              testId="filter-start-date" />
            <FilterInput label="Hasta" type="date" value={dateRange.end}
              onChange={(v) => setDateRange((r) => ({ ...r, end: v }))}
              testId="filter-end-date" />
          </>
        )}

        {/* Attribute filters — grow horizontally to fill remaining space */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end min-w-[320px]">
          <FilterMulti label="Marca" values={filters.brand}
            onChange={(vals) => setFilters((f) => ({ ...f, brand: vals }))}
            items={options.brands} testId="filter-brand" />
          <FilterMulti label="Modelo" values={filters.model}
            onChange={(vals) => setFilters((f) => ({ ...f, model: vals }))}
            items={options.models} disabled={!filters.brand.length}
            disabledLabel="Elige marca primero" testId="filter-model" />
          <FilterMulti label="Sucursal" values={filters.branch}
            onChange={(vals) => setFilters((f) => ({ ...f, branch: vals }))}
            items={options.branches} testId="filter-branch" />
          <FilterMulti label="Unidad" values={filters.business_unit}
            onChange={(vals) => setFilters((f) => ({ ...f, business_unit: vals }))}
            items={options.business_units} testId="filter-business-unit" />
          <FilterMulti label="Canal" values={filters.channel || []}
            onChange={(vals) => setFilters((f) => ({ ...f, channel: vals }))}
            items={options.channels || []} testId="filter-channel" />
          <FilterMulti label="Campaña" values={filters.campaign || []}
            onChange={(vals) => setFilters((f) => ({ ...f, campaign: vals }))}
            items={options.campaigns || []} testId="filter-campaign" />
        </div>

        {activeFilters > 0 && (
          <button onClick={reset}
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-2 rounded-md"
            style={{
              background: "#fff", border: "1px solid #d4d4d8", color: "#374151",
              cursor: "pointer", marginBottom: 1,
            }}
            data-testid="filter-reset">
            <X size={12} /> Limpiar ({activeFilters})
          </button>
        )}

        {onResetAll && (
          <button onClick={onResetAll}
            className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2.5 py-2 rounded-md transition-all"
            style={{
              background: "#0f172a", color: "#f4c400",
              border: "1px solid #0f172a",
              cursor: "pointer", marginBottom: 1,
              boxShadow: "0 2px 6px rgba(15,23,42,0.15)",
            }}
            title="Reinicia período, rango de fechas y todos los filtros"
            data-testid="filter-reset-all">
            <RotateCcw size={12} /> Reiniciar
          </button>
        )}

        <div className="flex items-center gap-1 text-[10px] font-bold text-gray-500 pb-2"
          style={{ flexShrink: 0 }}>
          <Filter size={12} />
          {activeFilters > 0
            ? `${activeFilters} valor${activeFilters > 1 ? "es" : ""} seleccionado${activeFilters > 1 ? "s" : ""}`
            : "Sin filtros"}
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-select dropdown with click-outside close. Trigger shows selection
 * count; body renders a scrollable checkbox list with a search input for
 * long collections (options.models can carry 200+ entries).
 */
function FilterMulti({ label, values, onChange, items, disabled, disabledLabel, testId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = query
    ? items.filter((it) => it.toLowerCase().includes(query.toLowerCase()))
    : items;
  const toggle = (it) => {
    if (values.includes(it)) onChange(values.filter((v) => v !== it));
    else onChange([...values, it]);
  };
  const summary = disabled ? disabledLabel
    : values.length === 0 ? "Todas"
    : values.length === 1 ? values[0]
    : `${values.length} seleccionadas`;

  return (
    <div className="flex flex-col gap-1 w-full">
      <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</label>
      <div ref={rootRef} className="relative" data-testid={testId}>
        <button type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className="text-xs font-bold px-2.5 py-1.5 rounded-md bg-white flex items-center justify-between gap-2 w-full"
          style={{
            border: "1px solid #e2e8f0",
            color: values.length ? "#111827" : "#64748b",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1, height: 32,
          }}
          data-testid={`${testId}-trigger`}>
          <span className="truncate">{summary}</span>
          <span style={{ fontSize: 9, color: "#94a3b8" }}>▾</span>
        </button>
        {!disabled && open && (
          <div className="absolute z-40 mt-1 rounded-lg bg-white shadow-lg"
            style={{ border: "1px solid #e2e8f0", minWidth: 220, maxWidth: 320, padding: 8 }}>
            {items.length > 8 && (
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar…" data-testid={`${testId}-search`}
                className="text-xs px-2 py-1 mb-2 w-full rounded"
                style={{ border: "1px solid #e2e8f0" }} />
            )}
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {filtered.length === 0 && (
                <div className="text-[11px] text-gray-400 p-2">Sin resultados</div>
              )}
              {filtered.map((it) => {
                const on = values.includes(it);
                return (
                  <label key={it}
                    className="flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer"
                    style={{ background: on ? "#fef9c3" : "transparent" }}
                    onMouseOver={(e) => { if (!on) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseOut={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}
                    data-testid={`${testId}-opt-${it.replace(/\s+/g, '-').toLowerCase()}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(it)}
                      style={{ cursor: "pointer" }} />
                    <span className="font-semibold text-gray-900 truncate" title={it}>{it}</span>
                  </label>
                );
              })}
            </div>
            {values.length > 0 && (
              <button onClick={() => onChange([])}
                className="mt-2 w-full text-[10px] font-bold py-1 rounded"
                style={{
                  background: "#f1f5f9", border: "1px solid #e2e8f0",
                  color: "#374151", cursor: "pointer",
                }}
                data-testid={`${testId}-clear`}>
                Limpiar selección
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterInput({ label, type, value, onChange, testId }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="text-xs font-bold px-2.5 py-1.5 rounded-md bg-white"
        style={{ border: "1px solid #e2e8f0", color: "#111827", height: 32, cursor: "pointer" }} />
    </div>
  );
}
